// TESSERAX — MODES DE JEU COURTS (Sprint / Ultra / Blitz).
//
// Le jeu était pensé comme un roguelite « longue session » : une bonne run dure
// 20–40 min (cf. core/pressure). C'est la forme PC ; la forme mobile/virale du
// genre, elle, est la session de 2–5 min, rejouable et partageable. On ajoute donc
// trois modes courts, bornés, orientés SKILL :
//   · Sprint — efface exactement 40 lignes, maximise le score (dense, nerveux).
//   · Ultra  — 120 s au compteur, maximise le score (boîte de temps classique).
//   · Blitz  — 60 s, gravité accélérée d'entrée (le mode frénétique).
//
// CHOIX D'ARCHITECTURE : tous les modes courts sont des « attaques de score » →
// on les CLASSE au score (compatibles tels quels avec le classement DESC, la carte
// de partage et la soumission existants). Seule la CONDITION DE FIN change (un
// nombre de lignes OU un temps). Aucun nouveau pipeline social : on réutilise tout.
// Ils sont aussi « purs » côté run : pas de reliques/missions/résonances en partie
// (meta:false) → classement plus équitable, et la modale de relique ne vient pas
// grignoter le chronomètre d'un mode chronométré.
//
// Module PUR : ni THREE ni DOM, pas de Math.random. main.ts lit le catalogue et
// applique terminaison/affichage/gravité sur ses variables de run existantes.

export type ModeId = 'free' | 'daily' | 'zen' | 'sprint' | 'ultra' | 'blitz' | 'strates';
export type ModeKind = 'endless' | 'lines' | 'timed';

export interface ModeDef {
  id: ModeId;
  kind: ModeKind;
  goalLines: number;   // kind 'lines' : fin après N lignes (0 sinon)
  timeMs: number;      // kind 'timed' : fin après N ms (0 sinon)
  levelOffset: number; // décalage de niveau pour la gravité (Blitz démarre vite)
  rise: boolean;       // montée d'instabilité active ? (survie uniquement)
  meta: boolean;       // reliques / missions / résonances EN PARTIE ?
  fold: boolean;       // LE PLI : puits à DEUX chambres couplées + effacement par UNION (cf. core/fold) ?
}

// LE PLI — SIGNATURE ULTIME, intégrée à TOUS les modes (décision direction, 2026-06) : le mode 4D
// autonome « Tesseract » a été DISSOUS, sa mécanique (deux tranches superposées Endroit/Envers + Pli
// touche B + effacement par union, cf. core/fold) est désormais le socle commun. `fold:true` partout :
// chaque mode est un puits 4D. Pur côté flux — le Pli ne consomme aucun tirage → déterminisme du défi
// du jour et rejeu bit-exact préservés (la séquence de pièces est inchangée).
export const MODES: Record<ModeId, ModeDef> = {
  // Montée d'instabilité RETIRÉE (rise:false partout) : on ne veut plus que le sol
  // pousse des blocs parasites. La menace de fin de partie revient au seul top-out
  // (le puits qui déborde) ; le défi de fin de run est désormais porté par
  // l'accélération de chute + l'enrichissement progressif des pièces complexes.
  free:   { id: 'free',   kind: 'endless', goalLines: 0,  timeMs: 0,      levelOffset: 0, rise: false, meta: true,  fold: true },
  daily:  { id: 'daily',  kind: 'endless', goalLines: 0,  timeMs: 0,      levelOffset: 0, rise: false, meta: true,  fold: true },
  zen:    { id: 'zen',    kind: 'endless', goalLines: 0,  timeMs: 0,      levelOffset: 0, rise: false, meta: true,  fold: true },
  sprint: { id: 'sprint', kind: 'lines',   goalLines: 40, timeMs: 0,      levelOffset: 0, rise: false, meta: false, fold: true },
  ultra:  { id: 'ultra',  kind: 'timed',   goalLines: 0,  timeMs: 120000, levelOffset: 0, rise: false, meta: false, fold: true },
  blitz:  { id: 'blitz',  kind: 'timed',   goalLines: 0,  timeMs: 60000,  levelOffset: 4, rise: false, meta: false, fold: true },
  // STRATES — ascension roguelite (cf. core/strates). Endless du point de vue du moteur (jamais de fin
  // lignes/temps) : ce sont les OBJECTIFS de strate qui font monter l'ascension ; rise:false ; méta active.
  strates: { id: 'strates', kind: 'endless', goalLines: 0, timeMs: 0, levelOffset: 0, rise: false, meta: true, fold: true },
};

// Modes courts proposés sur l'accueil (ordre d'affichage).
export const SHORT_MODES: ModeId[] = ['sprint', 'ultra', 'blitz'];

export function modeDef(id: string): ModeDef {
  return (MODES as Record<string, ModeDef>)[id] || MODES.free;
}

// Run terminée « naturellement » (objectif atteint) ? Renvoie la raison, ou ''.
// Le top-out (puits qui déborde) reste géré séparément par main.ts → fin
// « instabilité critique ». Ici on ne décrit que la fin POSITIVE des modes bornés.
export function modeComplete(def: ModeDef, lines: number, elapsedMs: number): '' | 'goal' | 'time' {
  if (def.kind === 'lines') return lines >= def.goalLines ? 'goal' : '';
  if (def.kind === 'timed') return elapsedMs >= def.timeMs ? 'time' : '';
  return '';
}

// Progression de l'objectif pour le HUD (compte à rebours). `label` est
// locale-neutre : un nombre (lignes restantes) ou « m:ss » (temps restant) ;
// main.ts l'habille d'un libellé i18n. endless → null (rien à afficher).
export interface ModeProgress { ratio: number; label: string; }
export function modeProgress(def: ModeDef, lines: number, elapsedMs: number): ModeProgress | null {
  if (def.kind === 'lines') {
    const left = Math.max(0, def.goalLines - lines);
    return { ratio: Math.min(1, def.goalLines > 0 ? lines / def.goalLines : 0), label: String(left) };
  }
  if (def.kind === 'timed') {
    const remMs = Math.max(0, def.timeMs - Math.max(0, elapsedMs));
    const s = Math.ceil(remMs / 1000);
    return { ratio: Math.min(1, def.timeMs > 0 ? elapsedMs / def.timeMs : 0), label: Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0') };
  }
  return null;
}
